import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useForm } from "react-hook-form";
import cx from "classnames";
import Store from "../../store";
import {
  SSOExternalUserGridListDto,
  SSOLoginAsExternalUserRequestDto,
  SSOLoginAsUserGridListDto,
  SSOLoginAsUserRequestDto,
} from "../../types";
import { Button } from "../../../../../components";
import { MessageResultState } from "../../../../../types/general-types";
import {
  setSecondarySessionKey,
  setSecondaryUser,
} from "../../../../../utils/SsoSession";
import { useHistory } from "react-router-dom";

interface IViewDetails {
  LoginAsUserStore?: Store;
  selectedRow: SSOLoginAsUserGridListDto | undefined;
  isShow: boolean;
  toggleModal: any;
  t: any;
  selectedExternalUser: SSOExternalUserGridListDto | undefined;
  isExternal: boolean;
}

const ExplanationModal: FC<IViewDetails> = inject("LoginAsUserStore")(
  observer((props: IViewDetails) => {
    const {
      LoginAsUserStore: store,
      isShow,
      toggleModal,
      selectedRow,
      selectedExternalUser,
      isExternal,
    } = props;
    const { t } = useTranslation();
    const { register, control, handleSubmit, errors } = useForm();
    const history = useHistory();

    const [data, setData] = useState<SSOLoginAsUserRequestDto>({
      explanation: "",
      personnelId: "",
    });
    const [externalData, setExternalData] =
      useState<SSOLoginAsExternalUserRequestDto>({
        explanation: "",
        userName: "",
      });

    useEffect(() => {
      if (selectedRow && data) {
        setData({ ...data, personnelId: selectedRow.personnelId });
      }
    }, [selectedRow]);

    useEffect(() => {
      if (selectedExternalUser) {
        setExternalData({
          ...externalData,
          userName: selectedExternalUser.username,
        });
      }
    }, [selectedExternalUser]);

    const page = useMemo(() => {
      return (
        <Modal isOpen={isShow} toggle={toggleModal} className="modal-lg">
          <ModalHeader toggle={toggleModal}>{t("Login As User")}</ModalHeader>
          <ModalBody>
            <>
              <div className="row mb-2">
                <div className="col-md-12">
                  <div className="mb-1">
                    <label style={{}} htmlFor="explanation">
                      {t("Explanation")}{" "}
                      <strong className="text-danger">*</strong>
                    </label>
                  </div>
                  <input
                    type="text"
                    value={
                      isExternal ? externalData?.explanation : data?.explanation
                    }
                    id={"explanation"}
                    name={"explanation"}
                    placeholder={t("Explanation")}
                    ref={register({
                      required: t("Please fill out this field.").toString(),
                      minLength: {
                        value: 10,
                        message: t(
                          "The given value must be {{value}} characters..",
                          { value: 10 }
                        ),
                      },
                    })}
                    className={cx("form-control ", {
                      "is-invalid": errors["explanation"],
                    })}
                    onChange={(event: any) => {
                      if (isExternal) {
                        setExternalData({
                          ...externalData,
                          explanation: event.target.value,
                        });
                      } else {
                        if (data) {
                          setData({
                            ...data,
                            explanation: event.target.value,
                          });
                        }
                      }
                    }}
                  />
                  {errors && errors["explanation"] && (
                    <div className="invalid-feedback d-block">
                      {errors["explanation"].message}
                    </div>
                  )}
                </div>
              </div>
            </>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-light mr-auto" onClick={toggleModal}>
              <i className="fas fa-times mr-2"></i>
              {t("Close")}
            </button>
            <Button
              className="btn"
              style={{
                backgroundColor: "#23486A",
                color: "#faf5f5",
              }}
              onClick={handleSubmit(async () => {
                if (isExternal) {
                  if (externalData) {
                    await store
                      ?.loginAsExternalUser(externalData)
                      .then((response) => {
                        if (response.state === MessageResultState.SUCCESS) {
                          setSecondarySessionKey(response.result.sessionKey);
                          setSecondaryUser(response.result.user);
                          history.push("portal-selection");
                        }
                      });
                  }
                } else {
                  if (data) {
                    await store?.loginAsUser(data).then((response) => {
                      if (response.state === MessageResultState.SUCCESS) {
                        setSecondarySessionKey(response.result.sessionKey);
                        setSecondaryUser(response.result.user);
                        history.push("portal-selection");
                      }
                    });
                  }
                }
              })}
              icon={<i className="fas fa-save mr-2"></i>}
              text={t("Login")}
              showConfirm={true}
            />
          </ModalFooter>
        </Modal>
      );
    }, [selectedRow, errors, data, externalData]);

    return <>{page}</>;
  })
);

export default ExplanationModal;
